import { useState } from 'react'

import { ShiftManagementLeaveModalDocument } from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { observer } from 'mobx-react'

import { SkeletonModal } from 'modules/core/components/Modal'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useRefreshShiftsRows } from 'modules/shift_management/hooks/refreshShiftsRows'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import type { Leave } from 'modules/shift_management/lib/types'
import type ShiftsRow from 'modules/shift_management/store/internal/models/ShiftsRow'
import EditLeaveModal from 'modules/timeoff/components/EditLeaveModal'
import LeaveRecommendationModal from 'modules/timeoff/components/LeaveRecommendationModal'
import RejectLeave from 'modules/timeoff/components/RejectLeave'
import { useLeaveRecommendationFeatureFlag } from 'modules/timeoff/hooks/useLeaveRecommendationFeatureFlag'
import { LeavesContextProvider } from 'modules/timeoff/lib/LeavesContextProvider'
import timeoffAllowances from 'modules/timeoff/store/collections/allowances'
import leaveTypes from 'modules/timeoff/store/collections/leaveTypes'
import policyTimelines from 'modules/timeoff/store/collections/timeoffPolicies/policyTimelines'

interface Props {
  leave: Leave
  onClose: () => void
  shiftsRow: ShiftsRow
}

const LeaveModal = ({ leave, onClose, shiftsRow }: Props) => {
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const { loading } = useAsyncData(async () => {
    await leaveTypes.fetch()
    await timeoffAllowances.fetch()

    if (hasFeatureFlag('timeoff_policy_timeline')) {
      await policyTimelines.fetch({
        data: {
          employee_id: leave.employeeId,
        },
      })
    }
  }, [])

  const [isRejecting, setIsRejecting] = useState(false)

  const { loading: ffLoading, enabled: isRecommendationEnabled } =
    useLeaveRecommendationFeatureFlag()

  const { refreshShiftsRows } = useRefreshShiftsRows()

  const { loading: leaveLoading, data } = useQuery(ShiftManagementLeaveModalDocument, {
    variables: {
      leaveId: Number(leave.id),
    },
  })

  if (loading || ffLoading || leaveLoading) {
    return <SkeletonModal />
  }

  if (!data?.apiCore.currentsConnection.edges[0]?.node.employee) {
    throw new Error('Current employee not found')
  }

  const currentEmployee = data.apiCore.currentsConnection.edges[0].node.employee

  const customResourceFieldsData =
    compact(
      data?.documents.foldersConnection.edges[0]?.node?.resourceFieldsConnection.edges.map(
        (edge) => edge.node
      )
    ) ?? []

  if (!leave.id) {
    return null
  }

  const leaveData = data?.timeoff.leave

  if (!leaveData) {
    throw new Error('Leave not found')
  }

  const leaveEmployee = leaveData.employee

  if (!leaveEmployee) {
    throw new Error('Leave employee not found')
  }

  const sickFolderId = data.documents.foldersConnection.edges[0]?.node?.id

  if (!sickFolderId) {
    throw new Error('Sick folder not found')
  }

  const showRecommendationModal = !ffLoading && isRecommendationEnabled

  const onChange = async () => {
    // TODO: Fetch and add the leave to the selected employee shift row
    await refreshShiftsRows([shiftsRow], [leave.employeeId])
    onClose()
  }

  return (
    <LeavesContextProvider>
      {!isRejecting &&
        (showRecommendationModal ? (
          <LeaveRecommendationModal
            currentEmployee={currentEmployee}
            customResourceFieldsData={customResourceFieldsData}
            delegatedApprover={leaveData.delegatedApproverAccess}
            fromShifts
            leaveEmployee={leaveEmployee}
            leaveId={leave.id}
            onChange={onChange}
            onClose={onClose}
            onReject={() => setIsRejecting(true)}
            sickFolderId={sickFolderId}
            leaveCanManageLeaveDocuments={leaveData._policies?.manageLeaveDocuments?.can ?? false}
            recommendation={leaveData.approvalRecommendation}
          />
        ) : (
          <EditLeaveModal
            currentEmployee={currentEmployee}
            customResourceFieldsData={customResourceFieldsData}
            delegatedApprover={leaveData.delegatedApproverAccess}
            leaveCanManageLeaveDocuments={false}
            fromShifts
            leaveEmployee={leaveEmployee}
            leaveId={leave.id}
            onChange={onChange}
            onClose={onClose}
            onReject={() => setIsRejecting(true)}
            sickFolderId={sickFolderId}
          />
        ))}
      {isRejecting && (
        <RejectLeave
          leaveId={leave.id}
          onClose={onClose}
          fromShifts
          leaveEmployeeId={leaveEmployee.id}
        />
      )}
    </LeavesContextProvider>
  )
}

export default observer(LeaveModal)
